export interface Tomo {
  /** Número visual de la tarjeta: '01' … '05'. */
  num: string
  /** Etapa del método a la que pertenece el tomo. */
  stage: string
  /** Nombre real del tomo. */
  title: string
  /** Resultado concreto en una línea. */
  outcome: string
  /** 3 cosas concretas que el odontólogo puede implementar. */
  points: string[]
}
export interface Pillar {
  title: string
  desc: string
}
export interface MethodStep {
  num: string
  name: string
  desc: string
}
export interface Bono {
  num: string
  /** Etiqueta editable. Reemplazar por el nombre definitivo cuando esté disponible. */
  label: string
  /** Portada del recurso. Cuando exista, se muestra en lugar del marco vacío. */
  cover?: string
}
export interface Benefit {
  title: string
  desc: string
}
export interface Faq {
  q: string
  a: string
}

/** Precio único vigente de la landing. */
/**
 * Formato monetario único de la landing: siempre "AR$ 00.000".
 * Nunca añadir "ARS" cuando ya se antepone "AR$".
 */
export const PRICE = {
  previous: 'AR$ 49.990',
  current: 'AR$ 19.990',
  note: 'Pago único',
} as const

export const PRODUCT_FACTS: string[] = [
  'Sistema en 5 tomos',
  '5 recursos extra de regalo',
  '100% digital',
  'Acceso inmediato',
]

/** El recorrido real del paciente dentro del consultorio. */
export const PATIENT_JOURNEY: string[] = [
  'Consulta',
  'WhatsApp',
  'Turno',
  'Asistencia',
  'Diagnóstico',
  'Presupuesto',
  'Tratamiento',
  'Cobro',
]

/** Las cuatro etapas del Método Consultorio Rentable. */
export const METHOD_STEPS: MethodStep[] = [
  {
    num: '01',
    name: 'Detectar',
    desc: 'Ver con números dónde se está perdiendo dinero: consultas sin respuesta, turnos caídos, presupuestos sin seguimiento y prácticas que no rinden.',
  },
  {
    num: '02',
    name: 'Convertir',
    desc: 'Mejorar dos momentos decisivos: de la consulta al turno, y del presupuesto al tratamiento aceptado.',
  },
  {
    num: '03',
    name: 'Recuperar',
    desc: 'Volver a activar el dinero dormido: presupuestos sin respuesta, tratamientos incompletos y pacientes que dejaron de venir.',
  },
  {
    num: '04',
    name: 'Rentabilizar',
    desc: 'Hacer que cada hora de sillón deje más: mejor agenda, mejor mix de pacientes y precios que cierran.',
  },
]

/** Los cinco tomos del sistema. */
export const TOMOS: Tomo[] = [
  {
    num: '01',
    stage: 'Detectar',
    title: 'El Consultorio que Rinde',
    outcome:
      'Saber, con números simples, cuánto entra realmente por hora de sillón y dónde se te está escapando.',
    points: [
      'Medir consultas, turnos, presupuestos y tratamientos aceptados',
      'Detectar las fugas concretas de tu consultorio',
      'Identificar qué prácticas te dejan dinero y cuáles te lo sacan',
    ],
  },
  {
    num: '02',
    stage: 'Convertir I',
    title: 'De Consulta a Turno',
    outcome:
      'Que la gente que te escribe o te llama termine sentada en el sillón, y que llegue el día del turno.',
    points: [
      'Respuestas de WhatsApp que ordenan la consulta y cierran el turno',
      'Reducir turnos perdidos y ausencias con confirmaciones simples',
      'Un circuito claro desde el primer mensaje hasta la primera visita',
    ],
  },
  {
    num: '03',
    stage: 'Convertir II',
    title: 'De Presupuesto a Tratamiento',
    outcome: 'Aumentar la aceptación de tratamientos sin bajar precios ni presionar al paciente.',
    points: [
      'Presentar el diagnóstico para que el paciente entienda el valor',
      'Armar presupuestos claros, por etapas y fáciles de decidir',
      'Manejar el "lo voy a pensar" y las objeciones de precio',
    ],
  },
  {
    num: '04',
    stage: 'Recuperar',
    title: 'El Dinero Dormido',
    outcome: 'Volver a facturar con pacientes y presupuestos que ya pasaron por tu consultorio.',
    points: [
      'Reactivar presupuestos que quedaron sin respuesta',
      'Retomar tratamientos que quedaron por la mitad',
      'Recuperar pacientes que hace meses no vuelven',
    ],
  },
  {
    num: '05',
    stage: 'Rentabilizar',
    title: 'El Consultorio Rentable',
    outcome: 'Ganar más sin sumar horas: mejor agenda, mejor mix y mejores precios.',
    points: [
      'Ordenar la agenda alrededor de las prácticas más rentables',
      'Revisar y actualizar precios con criterio',
      'Reducir progresivamente la dependencia de obras sociales',
    ],
  },
]

/** Las fugas: por dónde se pierde hoy el dinero que el consultorio ya genera. */
export const SYSTEM_PILLARS: Pillar[] = [
  { title: 'Consultas', desc: 'Mensajes y llamados que nunca terminan en un turno agendado.' },
  { title: 'Ausencias', desc: 'Turnos dados que después se caen o no se reprograman.' },
  { title: 'Presupuestos', desc: 'Planes presentados que quedan esperando una respuesta.' },
  { title: 'Tratamientos', desc: 'Planes que empiezan y quedan interrumpidos por la mitad.' },
  { title: 'Rentabilidad', desc: 'Horas de sillón ocupadas con prácticas que casi no dejan.' },
]

export const TOMO_FEATURES: string[] = [
  'Escrito en lenguaje simple, sin jerga de marketing',
  'Ejemplos reales de consultorio odontológico',
  'Guiones y textos listos para usar en WhatsApp',
  'Planillas y checklists de implementación',
  'Indicadores concretos para medir cada mes',
  'Aplicable en consultorios chicos y medianos',
  'Se lee desde el celular, la tablet o la computadora',
  'Todo orientado a rentabilidad, no a teoría',
]

/**
 * Los cinco recursos extra de regalo. Los nombres definitivos y las portadas se
 * cargan aquí (campo `cover`) en cuanto estén disponibles, sin tocar el componente.
 */
export const BONOS: Bono[] = [
  { num: '01', label: 'Recurso extra 1' },
  { num: '02', label: 'Recurso extra 2' },
  { num: '03', label: 'Recurso extra 3' },
  { num: '04', label: 'Recurso extra 4' },
  { num: '05', label: 'Recurso extra 5' },
]

/** Situaciones cotidianas en las que el odontólogo tiene que reconocerse. */
export const PAIN_BLOCKS: string[] = [
  'Trabajás muchas horas, la agenda se llena, pero a fin de mes el número no cierra como debería.',
  'Llegan consultas por WhatsApp, se responden como se puede y muchas nunca terminan en un turno.',
  'Presentás un presupuesto, el paciente dice que lo va a pensar y nunca más vuelve a aparecer.',
  'Tenés tratamientos que quedaron por la mitad y nadie hizo seguimiento.',
  'Gran parte de tu agenda depende de obras sociales que pagan poco y tarde.',
  'Sabés que hay pacientes que hace más de un año que no vuelven, pero no tenés forma de reactivarlos.',
  'Nadie te enseñó a mirar tu consultorio con números: cuánto entra por hora, qué práctica rinde y qué no.',
]

export const BENEFITS: Benefit[] = [
  {
    title: 'Convertí mejor las consultas',
    desc: 'Que los mensajes y llamados que ya recibís terminen en turnos agendados, con un circuito de respuesta claro.',
  },
  {
    title: 'Perdé menos turnos',
    desc: 'Menos ausencias y cancelaciones de último momento con confirmaciones y recordatorios que se aplican todos los días.',
  },
  {
    title: 'Aumentá la aceptación',
    desc: 'Presentá diagnósticos y presupuestos de forma que el paciente entienda el valor y decida avanzar.',
  },
  {
    title: 'Recuperá lo que quedó abierto',
    desc: 'Presupuestos sin respuesta, tratamientos incompletos y pacientes inactivos que hoy son dinero dormido.',
  },
  {
    title: 'Mejorá el mix de pacientes',
    desc: 'Aumentá progresivamente la proporción de pacientes particulares sin resignar de golpe tu volumen actual.',
  },
  {
    title: 'Rentabilizá cada hora',
    desc: 'Que la misma cantidad de horas de sillón te deje más, ordenando agenda, prácticas y precios.',
  },
]

export const AUDIENCE_POINTS: string[] = [
  'Sos odontólogo/a y tenés tu propio consultorio, solo o con un equipo chico.',
  'Trabajás mucho, pero sentís que la rentabilidad no acompaña las horas que ponés.',
  'Recibís consultas por WhatsApp y no tenés un circuito claro para convertirlas en turnos.',
  'Presentás presupuestos y una buena parte queda sin respuesta.',
  'Dependés demasiado de obras sociales o prepagas y querés cambiar ese mix.',
  'Querés ordenar el consultorio como negocio, sin dejar de ser odontólogo.',
  'Buscás un sistema práctico para aplicar, no teoría de marketing.',
  'Querés crecer sin tener que atender cada vez más pacientes.',
]

export const DISCIPLINES: string[] = [
  'Odontología general',
  'Estética dental',
  'Ortodoncia',
  'Implantes',
  'Endodoncia',
  'Periodoncia',
  'Odontopediatría',
  'Prótesis',
]

export const TRANSFORM_BEFORE: string[] = [
  'No sabés cuánto deja cada hora de sillón',
  'Las consultas se responden como se puede',
  'Los presupuestos quedan sin seguimiento',
  'Los pacientes inactivos no vuelven',
  'La agenda depende de obras sociales',
  'Más horas de trabajo, misma rentabilidad',
]

export const TRANSFORM_AFTER: string[] = [
  'Medís lo que entra y lo que se pierde',
  'Tenés un circuito claro de consulta a turno',
  'Cada presupuesto tiene seguimiento',
  'Reactivás pacientes y tratamientos abiertos',
  'Crece la proporción de pacientes particulares',
  'La misma agenda te deja más dinero',
]

/** Tareas del sistema que pueden delegarse a secretaria o asistente. */
export const DELEGATION_TASKS: string[] = [
  'Responder las consultas de WhatsApp con un guion definido',
  'Agendar turnos y confirmar el día previo',
  'Reprogramar ausencias en lugar de perderlas',
  'Hacer seguimiento de presupuestos presentados',
  'Reactivar pacientes que hace meses no vuelven',
  'Registrar los indicadores del mes en una planilla simple',
]

export const OFFER_INCLUDES: string[] = [
  '5 tomos del sistema completo',
  '5 recursos extra de regalo',
  'Acceso inmediato',
  'Material 100% digital',
  'Guiones y planillas listos para usar',
  'Acceso desde cualquier dispositivo',
  'Pago único, sin suscripciones',
]

export const FAQS: Faq[] = [
  {
    q: '¿Necesito saber de marketing o de administración?',
    a: 'No. El Método Consultorio Rentable está escrito para odontólogos que nunca estudiaron gestión ni marketing. Todo se explica en lenguaje simple, con pasos concretos y planillas para completar.',
  },
  {
    q: '¿Es un curso con clases en vivo?',
    a: 'No. Es un sistema de implementación en 5 tomos digitales, más 5 recursos extra de regalo. No hay horarios ni cursadas: lo leés y lo aplicás a tu ritmo, desde el celular o la computadora.',
  },
  {
    q: '¿Sirve para un consultorio chico?',
    a: 'Sí. Está pensado justamente para consultorios propios, chicos y medianos. Cuanto más chica es la estructura, más pesa cada consulta perdida y cada presupuesto sin respuesta.',
  },
  {
    q: '¿Tengo que dejar las obras sociales?',
    a: 'No. El sistema no plantea abandonarlas de golpe. Plantea reducir progresivamente la dependencia y mejorar el mix de pacientes, para que la parte particular crezca sin que se te caiga la agenda.',
  },
  {
    q: '¿Puedo aplicarlo si trabajo solo, sin secretaria?',
    a: 'Sí. El sistema está diseñado para que lo pueda aplicar el odontólogo solo. Ahora bien, muchas de las tareas son delegables: si tenés secretaria o asistente, gran parte del circuito puede quedar en sus manos.',
  },
  {
    q: '¿En cuánto tiempo puedo ver resultados?',
    a: 'Depende de tu punto de partida, pero las acciones de Detectar y Recuperar suelen ser las más rápidas: medir dónde se pierde y reactivar presupuestos y pacientes que ya pasaron por tu consultorio.',
  },
  {
    q: '¿Cómo recibo el material?',
    a: 'Después de la compra recibís el acceso al material digital para empezar a implementarlo de inmediato.',
  },
]
